import { useEffect, useRef } from "react";

// The drawing uses a 20 × 26 grid, scaled to the element's size.
// Minimal geometric glass: two triangles meeting at a fine neck.
const GRID_WIDTH = 20;
const GLASS = new Path2D("M3 2H17L10.6 12.4V13.6L17 24H3L9.4 13.6V12.4Z");
// Inside of each bulb, kept clear of the glass so the edges stay crisp
const TOP_BULB = new Path2D("M4.9 3H15.1L10.25 10.4H9.75Z");
const BOTTOM_BULB = new Path2D("M9.75 15.6H10.25L15.1 23H4.9Z");
const NECK = 10.4; // where sand leaves the top bulb
const TOP_FULL = 7.4; // height of the sand in a full top bulb (filled to the top)
const FLOOR = 23; // bottom of the lower bulb
const BOTTOM_HEIGHT = 7.4; // floor to neck
const PILE_SHARE = 0.85; // how much of the bottom bulb the finished pile fills
const GRAINS = 3; // grains falling through the neck at once
const SAND = "#fff";

/*
 * Small hourglass showing how long the current photo has left. The sand is
 * drawn fresh every frame, so it moves perfectly smoothly:
 * - the top drains like a real hourglass (steady flow, so the surface sinks
 *   slowly while the bulb is wide and faster near the neck), with a little
 *   dip where the sand funnels into the neck
 * - single grains fall through the neck
 * - a rounded mound grows at the bottom
 * It runs for `duration` ms and stops while `paused` (Releases over it, tab
 * hidden, time frozen). Each new photo remounts it (key) and it flips over:
 * clockwise going forward (flip = 1), counter-clockwise going back (-1).
 * Unlike the old progress lines, it doesn't reveal how many photos there are.
 */
function Hourglass({ duration, paused, flip }) {
  const canvasRef = useRef(null);
  const elapsedRef = useRef(0); // ms of running time so far (not counting pauses)

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    // At least 2× so the thin lines stay smooth even on regular screens
    const ratio = Math.max(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const scale = (width / GRID_WIDTH) * ratio;

    const draw = () => {
      const progress = Math.min(elapsedRef.current / duration, 1);
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(scale, 0, 0, scale, 0, 0);
      context.fillStyle = SAND;

      // Top: steady flow into a cone, so the level is the square root of what's left
      const level = Math.sqrt(1 - progress) * TOP_FULL;
      if (level > 0.05) {
        const surface = NECK - level;
        const dip = Math.min(0.8, level * 0.25) * Math.min(1, progress * 40);
        context.save();
        context.clip(TOP_BULB);
        context.beginPath();
        context.moveTo(3, surface);
        context.quadraticCurveTo(10, surface + dip * 2, 17, surface);
        context.lineTo(17, NECK + 1);
        context.lineTo(3, NECK + 1);
        context.fill();
        context.restore();
      }

      // Bottom: a low mound growing from the floor. The bulb narrows going
      // up, so the same flow raises the level faster as it fills.
      const pile = BOTTOM_HEIGHT * (1 - Math.sqrt(1 - progress * PILE_SHARE));
      const peak = FLOOR - pile;
      if (pile > 0.05) {
        const mound = Math.min(0.9, pile * 0.35);
        const sides = peak + mound;
        context.save();
        context.clip(BOTTOM_BULB);
        context.beginPath();
        context.moveTo(3, FLOOR + 1);
        context.lineTo(3, sides);
        context.quadraticCurveTo(10, 2 * peak - sides, 17, sides);
        context.lineTo(17, FLOOR + 1);
        context.fill();
        context.restore();
      }

      // Falling grains (plus a faint thread), fading out as the top runs empty
      const flowing = progress > 0 && progress < 1 ? Math.min(1, (1 - progress) * 30) : 0;
      if (flowing > 0) {
        const top = NECK + 0.6;
        const fall = peak - 0.2 - top;
        context.globalAlpha = flowing * 0.35;
        context.fillRect(9.85, top, 0.3, fall);
        context.globalAlpha = flowing;
        const seconds = elapsedRef.current / 1000;
        for (let i = 0; i < GRAINS; i++) {
          // Each grain loops from the neck to the pile, speeding up as it falls
          const along = (seconds * 1.6 + i / GRAINS) % 1;
          const y = top + fall * along * along;
          const x = 10 + Math.sin(i * 12.9) * 0.12;
          context.beginPath();
          context.arc(x, y, 0.36, 0, Math.PI * 2);
          context.fill();
        }
        context.globalAlpha = 1;
      }

      // Glass on top
      context.strokeStyle = "#fff";
      context.lineWidth = 1.2;
      context.lineJoin = "round";
      context.lineCap = "round";
      context.stroke(GLASS);
    };

    if (paused) {
      draw();
      return undefined;
    }
    let frame = null;
    let last = performance.now();
    const step = (now) => {
      elapsedRef.current += now - last;
      last = now;
      draw();
      if (elapsedRef.current < duration) frame = requestAnimationFrame(step);
    };
    draw();
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [duration, paused]);

  return (
    <span
      className={`gallery-hourglass ${flip ? "is-flipping" : ""} ${flip === -1 ? "is-back" : ""}`}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
    </span>
  );
}

export default Hourglass;
